// Apps Script web app that appends each submission to the B4B Google Sheet.
// Setup and redeploy steps: docs/large-order-form-sheet.md

// Apps Script is slow to answer — cold starts, the mail send, and the
// googleusercontent redirect all land in the shopper's wait. The row is written
// early in doPost, so past this point a slow answer means latency far more
// often than failure, and a spinner is the worse outcome.
const RESPONSE_TIMEOUT_MS = 8000;
const ENDPOINT =
  'https://script.google.com/macros/s/AKfycbzDIVjtVD93-cL6OsQwqi0TTaPUFnqn8K9sPsYLkLUJ1Ux1XocQdEJ5kH8SRzXPW5zq/exec';
const TOKEN = 'Wf9CsSXWZaD1LdG2RKqYPAkn13GDAkVQr9058w55E/Y=';

/**
 * Large order intake form (BS-8472).
 *
 * Validates required text, textarea and radio-group fields, then posts them to
 * the Apps Script web app, which appends one row per submission to the B4B
 * Google Sheet (BS-8473). The thank-you state is shown only once the script
 * confirms the row was written.
 * @returns {void}
 */
export default function loadLargeOrderForm() {
  const form = document.querySelector('#largeOrderForm');
  if (!form) return;

  const submitButton = form.querySelector('#largeOrderSubmit');
  const intro = document.querySelector('.large-order-form__intro');
  const thankYou = document.querySelector('#largeOrderThankYou');
  const honeypot = form.querySelector('#large_order_website_url');
  const formError = form.querySelector('.large-order-form__error--form');

  // Same-day or past is never a valid needed-by date, so the picker starts
  // tomorrow. `min` greys out the earlier days and makes checkValidity() fail,
  // so the existing validation covers it. Set here rather than in Liquid so it
  // follows the shopper's own clock and can't be served stale from a cached
  // render. `setDate` rolls the month and year over on its own, and `en-CA` is
  // the shortest way to get local-time YYYY-MM-DD.
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const neededByInput = form.querySelector('#NeededBy');
  neededByInput.min = tomorrow.toLocaleDateString('en-CA');

  /**
   * Writes (or clears) the error message belonging to a field's group.
   * @param {Element} field
   * @param {string} message
   * @returns {void}
   */
  const setGroupError = (field, message) => {
    const group = field.closest('.grid--input-field-group');
    const errorTarget = group?.querySelector('.large-order-form__error');
    if (errorTarget) errorTarget.textContent = message;
  };

  /**
   * Shows or clears the form-level error. Toggling the attribute lets the
   * styling key off a real state instead of an empty element.
   * @param {string} message
   * @returns {void}
   */
  const setFormError = (message) => {
    formError.innerHTML = message;
    formError.toggleAttribute('data-visible', Boolean(message));
  };

  /**
   * Marks every empty required field and returns whether the form passes.
   * Radio groups are keyed off `data-required-group` on their fieldset.
   * @returns {boolean}
   */
  const validate = () => {
    let isValid = true;

    for (const field of form.querySelectorAll(
      'input[required]:not([type="radio"]), textarea[required]'
    )) {
      const fieldIsValid = field.checkValidity();
      field.classList.toggle('invalid', !fieldIsValid);
      if (field.tagName === 'TEXTAREA') {
        setGroupError(field, fieldIsValid ? '' : 'This field is required.');
      }
      if (!fieldIsValid) isValid = false;
    }

    for (const group of form.querySelectorAll('[data-required-group]')) {
      const groupName = group.dataset.requiredGroup;
      const isAnswered = form.querySelector(
        `input[name="${groupName}"]:checked`
      );
      const errorTarget = group.querySelector('.large-order-form__error');
      if (errorTarget) {
        errorTarget.textContent = isAnswered ? '' : 'Please select an option.';
      }
      if (!isAnswered) isValid = false;
    }

    return isValid;
  };

  /**
   * Posts the submission to the Apps Script web app. No-ops when no endpoint
   * is configured on the section.
   *
   * Sent as urlencoded rather than JSON on purpose: Apps Script does not answer
   * CORS preflight, so the request has to stay a "simple" one.
   * @param {URLSearchParams} payload
   * @returns {Promise<void>}
   */
  const submitRequest = async (payload) => {
    if (!ENDPOINT) throw new Error('No endpoint configured.');

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), RESPONSE_TIMEOUT_MS);

    let response;
    try {
      response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
        signal: controller.signal,
      });
    } catch (error) {
      // Timed out waiting for the answer, not for the request to be accepted.
      // Same reasoning as an unreadable body below: the script has it.
      if (error.name === 'AbortError') return;
      throw error;
    } finally {
      clearTimeout(timeout);
    }

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    // Apps Script answers 200 even when it refuses the write (bad token, lock
    // contention), so an explicit refusal in the body has to surface.
    const result = await response.json().catch(() => null);
    if (result && result.ok !== true) {
      throw new Error(`Rejected by the script: ${result.error || 'unknown'}`);
    }

    // An unreadable body is not a failure. Apps Script redirects its response
    // through script.googleusercontent.com, and that hop is not reliably
    // readable cross-origin, so a 200 we cannot parse still means the script
    // ran. Telling the shopper it failed would be wrong, and would invite a
    // duplicate submission for a row that already landed.
  };

  submitButton.addEventListener('click', async (event) => {
    event.preventDefault();
    if (submitButton.disabled) return;

    // Honeypot filled means a bot — fail silently so it can't tune its input.
    if (honeypot.value !== '') return;

    setFormError('');
    if (!validate()) return;

    const formData = new FormData(form);
    formData.delete('website_url');
    // Timestamp is stamped server-side; the hidden input is Liquid render time.
    formData.delete('Timestamp');
    formData.delete('blank');
    if (TOKEN) formData.append('token', TOKEN);
    const payload = new URLSearchParams(formData);

    submitButton.disabled = true;
    const originalButtonText = submitButton.textContent.trim();
    submitButton.textContent = 'Submitting...';

    try {
      await submitRequest(payload);
      window.dataLayer?.push({ event: 'dl_large_order_form_submit' });
      form.hidden = true;
      intro.hidden = true;
      thankYou.hidden = false;
    } catch (error) {
      submitButton.disabled = false;
      submitButton.textContent = originalButtonText;
      setFormError(
        'We couldn&rsquo;t submit your request. Please try again, or email sales@brooklinenbusiness.com and we&rsquo;ll take it from there.'
      );
      throw new Error(`Large order form submission failed. ${error}`);
    }
  });
}
